import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickAPISetup = () => {
  const [userLimits, setUserLimits] = useState(null);
  
  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const response = await axios.get(`${API}/user/limits`);
        setUserLimits(response.data);
      } catch (err) {
        console.error('Error fetching limits:', err);
        // Default to trial limits
        setUserLimits({
          allowed_platforms: ['monetizze', 'hotmart'],
          is_unlimited: false,
          is_admin: false,
          plan: 'free_trial'
        });
      }
    };
    fetchLimits();
  }, []);

  const isPlatformAllowed = (platformKey) => {
    if (!userLimits) return false;
    if (userLimits.is_unlimited || userLimits.is_admin) return true;
    if (userLimits.allowed_platforms === 'all') return true;
    return userLimits.allowed_platforms.includes(platformKey.toLowerCase());
  };

  // Carregar dados do onboarding se existirem
  const loadOnboardingData = () => {
    try {
      const onboardingData = localStorage.getItem('monetizemaster_onboarding');
      if (onboardingData) {
        const parsed = JSON.parse(onboardingData);
        
        // Mapear dados do onboarding para o formato da página de setup
        const mappedData = {
          clickbank: { 
            api_key: parsed.clickbank?.apiKey || '', 
            dev_key: parsed.clickbank?.developerKey || '' 
          },
          monetizze: { 
            api_token: parsed.monetizze?.token || '' 
          },
          hotmart: { 
            client_id: parsed.hotmart?.clientId || '', 
            client_secret: parsed.hotmart?.clientSecret || '' 
          },
          amazon: { 
            access_key: parsed.amazon?.accessKey || '', 
            secret_key: parsed.amazon?.secretKey || '', 
            associate_tag: parsed.amazon?.associateTag || '', 
            region: parsed.amazon?.region || 'US' 
          },
          shopee: { partner_id: '', partner_key: '', shop_id: '' },
          magalu: { affiliate_id: '', api_token: '', store_name: '' },
          mercadolivre: { app_id: '', secret_key: '', access_token: '', site_id: 'MLB' },
          awin: { publisher_id: '', api_token: '', region: 'Global' }
        };
        
        // Detectar quais plataformas foram configuradas
        const configured = [];
        if (parsed.clickbank?.apiKey) configured.push('clickbank');
        if (parsed.monetizze?.token) configured.push('monetizze');
        if (parsed.hotmart?.clientId && parsed.hotmart?.clientSecret) configured.push('hotmart');
        if (parsed.amazon?.accessKey && parsed.amazon?.secretKey) configured.push('amazon');
        
        return { keys: mappedData, configured };
      }
    } catch (error) {
      console.error('Erro ao carregar dados do onboarding:', error);
    }
    
    return {
      keys: {
        clickbank: { api_key: '', dev_key: '' },
        monetizze: { api_token: '' },
        hotmart: { client_id: '', client_secret: '' },
        amazon: { access_key: '', secret_key: '', associate_tag: '', region: 'US' },
        shopee: { partner_id: '', partner_key: '', shop_id: '' },
        magalu: { affiliate_id: '', api_token: '', store_name: '' },
        mercadolivre: { app_id: '', secret_key: '', access_token: '', site_id: 'MLB' },
        awin: { publisher_id: '', api_token: '', region: 'Global' }
      },
      configured: []
    };
  };

  const initialData = loadOnboardingData();
  const [activeTab, setActiveTab] = useState('clickbank');
  const [apiKeys, setApiKeys] = useState(initialData.keys);
  const [savedPlatforms, setSavedPlatforms] = useState(initialData.configured);

  const platforms = {
    clickbank: {
      name: 'ClickBank',
      description: 'Comissão até 75% • Cookie 60 dias',
      color: 'bg-green-500',
      fields: [
        { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Sua API Key do ClickBank' },
        { key: 'dev_key', label: 'Developer Key (Opcional)', type: 'password', placeholder: 'Developer Key' }
      ]
    },
    monetizze: {
      name: 'Monetizze', 
      description: 'Plataforma brasileira • Comissão até 80%',
      color: 'bg-blue-500',
      fields: [
        { key: 'api_token', label: 'Token/Chave de API', type: 'password', placeholder: 'Token da Monetizze (Ferramentas → API)' }
      ]
    },
    hotmart: {
      name: 'Hotmart',
      description: 'Produtos digitais • Comissão até 70%',
      color: 'bg-orange-500',
      fields: [
        { key: 'client_id', label: 'Client ID', type: 'password', placeholder: 'Client ID do Hotmart' },
        { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Client Secret' }
      ]
    },
    amazon: {
      name: 'Amazon Associates',
      description: 'Milhões de produtos • Comissão até 10%',
      color: 'bg-yellow-500',
      fields: [
        { key: 'access_key', label: 'Access Key', type: 'password', placeholder: 'AWS Access Key' },
        { key: 'secret_key', label: 'Secret Key', type: 'password', placeholder: 'AWS Secret Key' },
        { key: 'associate_tag', label: 'Associate Tag', type: 'text', placeholder: 'seu-tag-de-afiliado' },
        { key: 'region', label: 'Região', type: 'select', options: ['US', 'BR', 'CA', 'UK', 'DE', 'FR'] }
      ]
    },
    shopee: {
      name: '🛒 Shopee Afiliados',
      description: 'E-commerce Ásia e América Latina • Comissão 3-15%',
      color: 'bg-orange-600',
      icon: '🛒',
      instructions: 'Acesse affiliate.shopee.com.br → API Settings',
      fields: [
        { key: 'partner_id', label: 'Partner ID', type: 'text', placeholder: 'Seu Partner ID da Shopee' },
        { key: 'partner_key', label: 'Partner Key', type: 'password', placeholder: 'Chave secreta da API' },
        { key: 'shop_id', label: 'Shop ID (Opcional)', type: 'text', placeholder: 'ID da loja (opcional)' }
      ]
    },
    magalu: {
      name: '🏪 Magazine Você (Magalu)',
      description: 'Maior varejista brasileiro • Comissão 2-8%',
      color: 'bg-blue-700',
      icon: '🏪',
      instructions: 'Acesse magazinevoce.com.br → Integrações → API',
      fields: [
        { key: 'affiliate_id', label: 'ID de Afiliado', type: 'text', placeholder: 'Seu ID no Magazine Você' },
        { key: 'api_token', label: 'Token de API', type: 'password', placeholder: 'Token do dashboard' },
        { key: 'store_name', label: 'Nome da Loja (Opcional)', type: 'text', placeholder: 'Nome da sua loja virtual' }
      ]
    },
    mercadolivre: {
      name: '💛 Mercado Livre Afiliados',
      description: 'Maior marketplace LATAM • Comissão 1-12%',
      color: 'bg-yellow-400',
      icon: '💛',
      instructions: 'Acesse afiliados.mercadolivre.com.br → API',
      fields: [
        { key: 'app_id', label: 'App ID', type: 'text', placeholder: 'ID da aplicação' },
        { key: 'secret_key', label: 'Secret Key', type: 'password', placeholder: 'Chave secreta' },
        { key: 'access_token', label: 'Access Token', type: 'password', placeholder: 'Token de acesso de afiliado' },
        { key: 'site_id', label: 'Site ID', type: 'select', options: ['MLB', 'MLA', 'MLM', 'MLC', 'MCO'], placeholder: 'MLB = Brasil' }
      ]
    },
    awin: {
      name: '🌐 Awin',
      description: 'Rede global • Grandes marcas • Comissão 2-20%',
      color: 'bg-indigo-600',
      icon: '🌐',
      instructions: 'Acesse awin.com → Tools → API',
      fields: [
        { key: 'publisher_id', label: 'Publisher ID', type: 'text', placeholder: 'Seu ID de publisher' },
        { key: 'api_token', label: 'API Token', type: 'password', placeholder: 'Token de autenticação' },
        { key: 'region', label: 'Região', type: 'select', options: ['Global', 'US', 'UK', 'DE', 'FR', 'BR'] }
      ]
    }
  };

  const handleInputChange = (platform, field, value) => {
    setApiKeys(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  const handleSave = (platform) => {
    const keys = apiKeys[platform];
    const requiredFields = platforms[platform].fields.filter(f => f.key !== 'dev_key' && f.key !== 'region' && f.key !== 'shop_id' && f.key !== 'store_name');
    
    // Check if required fields are filled
    const hasRequiredFields = requiredFields.every(field => keys[field.key] && keys[field.key].trim() !== '');
    
    if (!hasRequiredFields) {
      alert('Por favor, preencha todos os campos obrigatórios!');
      return;
    }

    // Salvar em savedPlatforms
    setSavedPlatforms(prev => {
      const newSaved = prev.filter(p => p !== platform);
      newSaved.push(platform);
      return newSaved;
    });
    
    // Sincronizar com localStorage do onboarding
    try {
      const onboardingData = localStorage.getItem('monetizemaster_onboarding');
      let data = onboardingData ? JSON.parse(onboardingData) : {};
      
      // Mapear de volta para formato do onboarding
      if (platform === 'clickbank') {
        data.clickbank = { apiKey: keys.api_key, developerKey: keys.dev_key };
      } else if (platform === 'monetizze') {
        data.monetizze = { token: keys.api_token };
      } else if (platform === 'hotmart') {
        data.hotmart = { clientId: keys.client_id, clientSecret: keys.client_secret };
      } else if (platform === 'amazon') {
        data.amazon = { 
          accessKey: keys.access_key, 
          secretKey: keys.secret_key, 
          associateTag: keys.associate_tag, 
          region: keys.region 
        };
      }
      
      localStorage.setItem('monetizemaster_onboarding', JSON.stringify(data));
    } catch (error) {
      console.error('Erro ao sincronizar com onboarding:', error);
    }
    
    alert(`✅ ${platforms[platform].name} configurado com sucesso!\n\n🔥 Suas APIs estão prontas para gerar receita 24/7!\n\nPróximo passo: Acesse o Dashboard em Tempo Real para monitorar suas comissões.`);
  };

  const handleDelete = (platform) => {
    if (!window.confirm(`Tem certeza que deseja excluir a configuração de ${platforms[platform].name}?`)) {
      return;
    }

    // Limpar campos da plataforma
    setApiKeys(prev => ({
      ...prev,
      [platform]: Object.keys(prev[platform]).reduce((acc, key) => {
        acc[key] = '';
        return acc;
      }, {})
    }));

    // Remover de savedPlatforms
    setSavedPlatforms(prev => prev.filter(p => p !== platform));

    // Remover do localStorage
    try {
      const onboardingData = localStorage.getItem('monetizemaster_onboarding');
      if (onboardingData) {
        let data = JSON.parse(onboardingData);
        delete data[platform];
        localStorage.setItem('monetizemaster_onboarding', JSON.stringify(data));
      }
    } catch (error) {
      console.error('Erro ao remover do onboarding:', error);
    }

    alert(`🗑️ ${platforms[platform].name} removido com sucesso!`);
  };

  const isPlatformSaved = (platform) => savedPlatforms.includes(platform);
  const currentPlatform = platforms[activeTab];

  // Verifica se o plano é trial
  const isTrialPlan = userLimits?.plan === 'free_trial';
  const isPlatformLocked = (platformKey) => !isPlatformAllowed(platformKey);

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          🚀 Configuração Rápida de APIs
        </h2>
        <p className="text-gray-600 mb-4">
          Configure suas chaves para começar a ganhar dinheiro automaticamente
        </p>
        
        {/* Aviso de limitação do plano */}
        {isTrialPlan && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-left max-w-2xl mx-auto mb-4">
            <p className="text-yellow-800 font-semibold mb-2">⚠️ Plano Teste Grátis (7 dias)</p>
            <p className="text-sm text-yellow-700">
              No período de teste você tem acesso a <strong>2 plataformas</strong>: Monetizze e Hotmart.
              <br />
              <a href="/upgrade" className="text-blue-600 hover:underline font-medium">
                Faça upgrade para liberar todas as 9 plataformas! →
              </a>
            </p>
          </div>
        )}
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-left max-w-2xl mx-auto">
          <p className="text-purple-900 font-semibold mb-2">🆕 Novas Plataformas Adicionadas!</p>
          <div className="grid grid-cols-2 gap-2 text-sm text-purple-800">
            <div>• 🛒 Shopee Afiliados</div>
            <div>• 🏪 Magazine Você (Magalu)</div>
            <div>• 💛 Mercado Livre</div>
            <div>• 🌐 Awin (Rede Global)</div>
          </div>
          <p className="text-xs text-purple-700 mt-2">
            Agora você pode vender produtos de <strong>9 plataformas diferentes</strong>!
          </p>
        </div>
      </div>

      {/* Platform Tabs - Dynamic rendering with lock indicator */}
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {Object.entries(platforms).map(([key, platform]) => {
          const isLocked = isPlatformLocked(key);
          return (
            <button
              key={key}
              onClick={() => !isLocked && setActiveTab(key)}
              disabled={isLocked}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors relative ${
                isLocked 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                  : activeTab === key
                    ? `${platform.color} text-white`
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${isPlatformSaved(key) ? 'ring-2 ring-green-400' : ''}`}
            >
              {isLocked && '🔒 '}
              {isPlatformSaved(key) && !isLocked && '✅ '}
              {platform.icon || ''} {platform.name.replace(/[🛒🏪💛🌐]/g, '').trim()}
            </button>
          );
        })}
      </div>

      {/* Active Platform Configuration - Fixed conditional rendering */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {currentPlatform.name}
            </h3>
            <p className="text-gray-600 text-sm">
              {currentPlatform.description}
            </p>
          </div>
          {isPlatformSaved(activeTab) && (
            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
              ✅ Configurado
            </span>
          )}
        </div>

        {/* Form Fields - Fixed keys */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {currentPlatform.fields.map((field, index) => (
            <div key={`${activeTab}-${field.key}-${index}`}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              {field.type === 'select' ? (
                <select
                  value={apiKeys[activeTab][field.key] || ''}
                  onChange={(e) => handleInputChange(activeTab, field.key, e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione...</option>
                  {field.options && field.options.map((option, optionIndex) => (
                    <option key={`${option}-${optionIndex}`} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  value={apiKeys[activeTab][field.key] || ''}
                  onChange={(e) => handleInputChange(activeTab, field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => handleSave(activeTab)}
            className={`flex-1 py-3 px-6 rounded-lg font-medium text-white transition-colors ${
              isPlatformSaved(activeTab)
                ? 'bg-green-600 hover:bg-green-700'
                : `${currentPlatform.color} hover:opacity-80`
            }`}
          >
            {isPlatformSaved(activeTab) ? '🔄 Atualizar Configuração' : '💾 Salvar e Ativar'}
          </button>
          
          {isPlatformSaved(activeTab) && (
            <button
              onClick={() => handleDelete(activeTab)}
              className="py-3 px-6 rounded-lg font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
            >
              🗑️ Excluir
            </button>
          )}
        </div>
      </div>

      {/* Status de Todas as Plataformas */}
      <div className="mt-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
          📊 Status de Todas as Plataformas
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(platforms).map(([key, platform]) => (
            <div 
              key={key}
              onClick={() => setActiveTab(key)}
              className={`text-center p-3 rounded-lg cursor-pointer transition-all ${
                isPlatformSaved(key) 
                  ? 'bg-green-50 border-2 border-green-500' 
                  : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-2xl mb-1">{platform.icon || '📦'}</div>
              <h4 className="font-medium text-xs text-gray-800">
                {platform.name.replace(/[🛒🏪💛🌐]/g, '').trim()}
              </h4>
              <p className={`text-xs mt-1 font-bold ${
                isPlatformSaved(key) ? 'text-green-600' : 'text-gray-500'
              }`}>
                {isPlatformSaved(key) ? '✅ Ativo' : '❌ Inativo'}
              </p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 text-center mt-3">
          💡 Clique em uma plataforma para configurar ou editar
        </p>
      </div>

      {/* Help Section */}
      <div className="mt-6 bg-blue-50 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">💡 Onde encontrar suas chaves:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <strong>ClickBank:</strong> clickbank.com → Settings → API Keys
          </div>
          <div>
            <strong>Hotmart:</strong> developers.hotmart.com → Criar App
          </div>
          <div>
            <strong>Monetizze:</strong> Painel → Ferramentas → API (só 1 token)
          </div>
          <div>
            <strong>Amazon:</strong> affiliate-program.amazon.com → API
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickAPISetup;